import { FormControl, Theme, useMediaQuery } from '@mui/material';
import { TimePicker, type TimePickerProps } from '@sm-dashboard/ui-components';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';
import { formatTimeString, parseTimeFormValue } from '../utils/timeValue';

const TIME_STRING_RE = /^\d{1,2}:\d{2}$/;

type InputTimeProps = Omit<TimePickerProps, 'value' | 'onChange'> & {
  name: string;
  label?: string;
  helperText?: string;
  defaultValue?: string;
  /** When unset, storage type follows the current field value (string, Dayjs, or Date). */
  saveAsString?: boolean;
};

export const InputTime = ({
  name,
  label,
  helperText,
  defaultValue,
  saveAsString,
  disabled,
  ...props
}: InputTimeProps) => {
  const { control } = useFormContext() || {};
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const emitChange = (value: unknown, date: Date | null, onChange: (next: unknown) => void) => {
    if (saveAsString === true) {
      onChange(formatTimeString(date) ?? '');
      return;
    }
    if (saveAsString === false) {
      onChange(date ? dayjs(date) : null);
      return;
    }
    if (value == null || value === '') {
      onChange(formatTimeString(date) ?? '');
      return;
    }
    if (dayjs.isDayjs(value)) {
      onChange(date ? dayjs(date) : null);
      return;
    }
    if (value instanceof Date) {
      onChange(date);
      return;
    }
    if (typeof value === 'string' && TIME_STRING_RE.test(value)) {
      onChange(formatTimeString(date) ?? '');
      return;
    }
    onChange(formatTimeString(date) ?? '');
  };

  return (
    <FormControl fullWidth>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TimePicker
            label={label || ''}
            value={parseTimeFormValue(value, defaultValue)}
            onChange={date => emitChange(value, date, onChange)}
            disabled={disabled}
            error={!!error}
            helperText={error?.message || helperText}
            {...props}
          />
        )}
      />
    </FormControl>
  );
};

export default InputTime;
